import * as React from 'react';

import {appHistory} from '../../../../../../history';

import {colors} from '../../colors/colors';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {InfoSquareIcon} from '../../icon/svg/info-square-icon';
import {PencilOutlineIcon} from '../../icon/svg/pencil-outline-icon';
import {PlayDoubleIcon} from '../../icon/svg/play-double-icon';
import {Stack} from '../../layout/stack/stack';
import {StatusBadge} from '../../status-badge/status-badge';
import {FormattedTimestamp} from '../../timestamp/formatted-timestamp';
import {Toolbar} from '../../toolbar/toolbar';
import {Body} from '../../typography/body';

import {getTimestampFormat} from './helpers';

import styles from './automated-email-row.module.css';

type Props = {
    id: string;
    title: string;
    sent: boolean;
    sendTime: string | null | undefined;
    scheduledTime: string | null | undefined;
    delay?: string | null | undefined;
    onViewMessage: (emailId: string) => void;
    onSendNow?: () => void;
    editUrl?: string;
};

export function AutomatedEmailRow(props: Props) {
    const {onViewMessage, onSendNow} = props;

    const [shouldShowToolbar, setShouldShowToolbar] = React.useState<boolean>(false);

    const statusBadgeLevel = React.useMemo(() => {
        if (props.sent) {
            return 'success';
        }

        return 'info';
    }, [props.sent]);

    const timestampFormat = React.useMemo(
        () => getTimestampFormat(Number(props.sendTime || props.scheduledTime)),
        [props.sendTime, props.scheduledTime]
    );

    const toolbarOptions = React.useMemo(() => {
        const options = [
            {
                id: 'view',
                icon: InfoSquareIcon,
                tooltipText: 'View message',
                onClick: () => {
                    onViewMessage(props.id);
                },
            },
        ];
        if (onSendNow) {
            options.push({
                id: 'send',
                icon: PlayDoubleIcon,
                tooltipText: 'Send now',
                onClick: onSendNow,
            });
        }
        if (props.editUrl) {
            options.push({
                id: 'edit',
                icon: PencilOutlineIcon,
                tooltipText: 'Edit message',
                onClick: () => appHistory.push(props.editUrl!),
            });
        }

        return options;
    }, [props.id, onViewMessage, onSendNow, props.editUrl]);

    return (
        <div
            className='pos-relative'
            onMouseOver={() => setShouldShowToolbar(true)}
            onMouseLeave={() => setShouldShowToolbar(false)}
        >
            <Stack isHorizontal={true} justifyContent='space-between' isFullWidth={true}>
                <Stack spacing={2}>
                    <Stack spacing={8} isHorizontal={true} alignItems='start'>
                        <Body isBold={true}>{props.title}</Body>
                        <StatusBadge
                            level={statusBadgeLevel}
                            text={props.sent ? 'Sent' : 'Scheduled'}
                            size='small'
                        />
                    </Stack>
                    <Stack spacing={4} isHorizontal={true} alignItems='center'>
                        {/* @ts-expect-error */}
                        <ClockRegularIcon size={16} color={colors.grey} wrapWithDiv={false} />
                        {props.sendTime || props.scheduledTime ? (
                            <Stack isHorizontal={true} spacing={4}>
                                {props.sendTime ? 'Sent' : 'Scheduled to send'}
                                <FormattedTimestamp
                                    datetime={Number(props.sendTime || props.scheduledTime)}
                                    format={timestampFormat}
                                />
                            </Stack>
                        ) : (
                            <Body size='small'>
                                {props.sent
                                    ? 'Sent'
                                    : `Scheduled to send ${
                                          props.delay
                                              ? `${props.delay} after the previous email`
                                              : 'soon'
                                      }`}
                            </Body>
                        )}
                    </Stack>
                </Stack>
            </Stack>
            {shouldShowToolbar && (
                <div className={styles['toolbar-container']}>
                    <Toolbar options={toolbarOptions} />
                </div>
            )}
        </div>
    );
}
